import { includesIgnoringAccents } from "@/functions/stringComparison";
import {
  PayloadAction,
  createAsyncThunk,
  createSlice,
  current,
} from "@reduxjs/toolkit";

import { APIStatus } from "../../api/APIStatus.type";
import * as privateApi from "../../api/privateApi";
import { getSbcSets } from "../../api/privateApi";
import {
  RepeatabilityMode,
  SBCRewardType,
} from "../../api/privateRequests/sbcs/getSbcSets";
import { IRootState } from "../store";
import { Platform } from "../user/userSlice";

export type SbcCategoryLabels =
  | "All"
  | "Favorites"
  | "Players"
  | "Upgrades"
  | "Challenges"
  | "Icons"
  | "Foundations"
  | "Hidden";

type SBCCategoryValues = 1 | 2 | 3 | 4 | 5;

export type SBCCategory = {
  label: SbcCategoryLabels;
  value?: SBCCategoryValues;
};

type SBCSetSortOptionValues =
  | "most-upvoted"
  | "price-high-to-low"
  | "price-low-to-high"
  | "in-progress"
  | "expiration"
  | "newest"
  | "meta-rating";

export type SBCSetSortOption = {
  label:
    | "Most Upvoted"
    | "Price - High to Low"
    | "Price - Low to High"
    | "In-progress"
    | "Expiration"
    | "Newest"
    | "Meta Rating";
  value: SBCSetSortOptionValues;
};

export const SBC_SET_CATEGORIES: SBCCategory[] = [
  { label: "All" },
  { label: "Favorites" },
  { label: "Players", value: 1 },
  { label: "Upgrades", value: 2 },
  { label: "Challenges", value: 3 },
  { label: "Icons", value: 4 },
  { label: "Foundations", value: 5 },
];

export type SbcSet = {
  reward: SBCRewardType;
  id: number;
  imageURL: string;
  name: string;
  description: string;
  pcPrice: number;
  psPrice: number;
  message: string;
  sbcsCount: number;
  startTime: number;
  endTime: number;
  upVotes: number;
  downVotes: number;
  isNew: boolean;
  isHidden: boolean;
  isFavorite: boolean;
  hasRefreshed: boolean;
  timeRemaining: string;
  repeatable: boolean;
  refreshInterval?: number;
  repeats: number;
  categoryId: number;
  repeatabilityMode: RepeatabilityMode;
  unsolvable?: boolean;
  solvedCount: number;
  solved?: boolean;
  wasUpvoted?: boolean;
  wasDownvoted?: boolean;
  remainingCost: number | null;
};

type SBCState = {
  sbcsSets: {
    data: SbcSet[];
    status: APIStatus;
  };
  currentCategory: SBCCategory;
  currentSort: SBCSetSortOption;
  searchTerm: string;
};

export type SetVote = "UPVOTE" | "DOWNVOTE";

const initialState: SBCState = {
  sbcsSets: {
    status: "idle",
    data: [],
  },
  currentCategory: { label: "All" },
  currentSort: {
    label: "Newest",
    value: "newest",
  },
  searchTerm: "",
};

export const markSbcSetSolved = createAsyncThunk(
  "mark-sbc-set-solved",
  async (
    {
      setId,
      didComplete,
    }: {
      setId: number;
      didComplete: boolean;
    },
    { dispatch },
  ) => {
    dispatch(updateSbcSetSolved({ setId, didComplete }));
    privateApi.markSbcSetSolved(setId, didComplete);
  },
);

export const markSbcSolved = createAsyncThunk(
  "update-sbc-solved",
  async (
    {
      setId,
      sbcId,
      solved,
    }: {
      setId: number;
      sbcId: number;
      solved: boolean;
    },
    { getState, dispatch },
  ) => {
    const state = getState() as IRootState;

    const sbc = state.sbcsReducer.sbcs.find((sbc) => sbc.id === sbcId);
    const platform = state.userReducer.user.platform || "Playstation";
    dispatch(
      updateSbcSolved({
        setId,
        solved,
        priceOfSbc: sbc.price,
        platfrom: platform,
      }),
    );
  },
);

export const hideSbcSet = createAsyncThunk(
  "hide-sbc-set",
  async (
    {
      setId,
      isHidden,
    }: {
      setId: number;
      isHidden: boolean;
    },
    { dispatch },
  ) => {
    dispatch(updateSbcSetHidden({ setId, isHidden }));
    privateApi.hideSbcSet(setId, isHidden);
  },
);
export const markSbcSetAsFavorite = createAsyncThunk(
  "hide-sbc-set",
  async (
    {
      setId,
      isFavorite,
    }: {
      setId: number;
      isFavorite: boolean;
    },
    { dispatch },
  ) => {
    dispatch(updateSbcSetFavorite({ setId, isFavorite }));
    privateApi.markSbcSetAsFavorite(setId, isFavorite);
  },
);

export const fetchSbcsSets = createAsyncThunk("sbc-sets", async () => {
  return getSbcSets();
});
const getCopyOfSet = (state: SBCState, setId: number) => {
  const sbcSetsCopy = [...current(state.sbcsSets.data)];
  let sbcSetIndex = sbcSetsCopy.findIndex((sbcSet) => sbcSet.id === setId);
  return {
    sbcSetIndex,
    sbcSetCopy: { ...sbcSetsCopy[sbcSetIndex] },
    sbcSetsCopy,
  };
};
const sbcSetsSlice = createSlice({
  name: "sbc-sets",
  initialState,
  reducers: {
    updateSbcSolved(
      state,
      action: PayloadAction<{
        setId: number;
        solved: boolean;
        priceOfSbc: number;
        platfrom: Platform;
      }>,
    ) {
      const {
        setId,
        solved: solvedPayload,
        priceOfSbc,
        platfrom,
      } = action.payload;
      const { sbcSetsCopy, sbcSetIndex, sbcSetCopy } = getCopyOfSet(
        state,
        setId,
      );
      if (solvedPayload && sbcSetCopy.solvedCount < +sbcSetCopy.sbcsCount) {
        sbcSetCopy.solvedCount += 1;
      } else {
        sbcSetCopy.solvedCount -= 1;
      }
      sbcSetCopy.solved = sbcSetCopy.solvedCount === +sbcSetCopy.sbcsCount;
      if (sbcSetCopy.remainingCost) {
        if (solvedPayload) {
          sbcSetCopy.remainingCost -= priceOfSbc;
        } else {
          if (sbcSetCopy.solvedCount === 0) {
            sbcSetCopy.remainingCost = null;
          } else {
            sbcSetCopy.remainingCost += priceOfSbc;
          }
        }
      } else {
        sbcSetCopy.remainingCost =
          platfrom === "Playstation"
            ? sbcSetCopy.psPrice - priceOfSbc
            : sbcSetCopy.pcPrice - priceOfSbc;
      }

      sbcSetsCopy[sbcSetIndex] = sbcSetCopy;
      state.sbcsSets.data = sbcSetsCopy;
    },
    updateSbcSetSolved(state, action) {
      const { setId, didComplete: solvedPayload } = action.payload;
      const { sbcSetsCopy, sbcSetIndex, sbcSetCopy } = getCopyOfSet(
        state,
        setId,
      );
      if (solvedPayload) {
        sbcSetCopy.solvedCount = +sbcSetCopy.sbcsCount;
        sbcSetCopy.remainingCost = 0;
      } else {
        sbcSetCopy.solvedCount = 0;
      }
      sbcSetCopy.solved = solvedPayload;
      sbcSetsCopy[sbcSetIndex] = sbcSetCopy;

      state.sbcsSets.data = sbcSetsCopy;
    },
    upvote(state, action) {
      const { setId } = action.payload;

      const sbcSets = state.sbcsSets.data;

      const sbcSetIndex = sbcSets.findIndex((sbcSet) => +sbcSet.id === setId);

      if (sbcSets[sbcSetIndex].wasUpvoted) {
        privateApi.undoVoteSBCSet({ setId, voteType: "UPVOTE" });
        sbcSets[sbcSetIndex].upVotes -= 1;
      } else {
        privateApi.voteSBCSet({ setId, voteType: "UPVOTE" });
        sbcSets[sbcSetIndex].upVotes += 1;
      }

      if (sbcSets[sbcSetIndex].wasDownvoted)
        sbcSets[sbcSetIndex].downVotes -= 1;

      sbcSets[sbcSetIndex].wasDownvoted = false;
      sbcSets[sbcSetIndex].wasUpvoted = !sbcSets[sbcSetIndex].wasUpvoted;
    },
    downvote(state, action) {
      const { setId } = action.payload;

      const sbcSets = state.sbcsSets.data;

      const sbcSetIndex = sbcSets.findIndex((sbcSet) => +sbcSet.id === setId);

      if (sbcSets[sbcSetIndex].wasDownvoted) {
        privateApi.undoVoteSBCSet({ setId, voteType: "DOWNVOTE" });
        sbcSets[sbcSetIndex].downVotes -= 1;
      } else {
        privateApi.voteSBCSet({ setId, voteType: "DOWNVOTE" });
        sbcSets[sbcSetIndex].downVotes += 1;
      }

      if (sbcSets[sbcSetIndex].wasUpvoted) sbcSets[sbcSetIndex].upVotes -= 1;

      sbcSets[sbcSetIndex].wasUpvoted = false;

      sbcSets[sbcSetIndex].wasDownvoted = !sbcSets[sbcSetIndex].wasDownvoted;
    },
    clearSbcSets(state) {
      state.sbcsSets.data = [];
    },
    updateCategory(state, action: PayloadAction<SBCCategory>) {
      state.currentCategory = action.payload;
    },
    updateSort(state, action: PayloadAction<SBCSetSortOption>) {
      state.currentSort = action.payload;
    },
    updateSearchTerm(state, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
    },
    updateSbcSetHidden(
      state,
      action: PayloadAction<{
        setId: number;
        isHidden: boolean;
      }>,
    ) {
      state.sbcsSets.data = state.sbcsSets.data.map((set) =>
        set.id === action.payload.setId
          ? { ...set, isHidden: action.payload.isHidden }
          : set,
      );
    },
    updateSbcSetFavorite(
      state,
      action: PayloadAction<{
        setId: number;
        isFavorite: boolean;
      }>,
    ) {
      state.sbcsSets.data = state.sbcsSets.data.map((set) =>
        set.id === action.payload.setId
          ? { ...set, isFavorite: action.payload.isFavorite }
          : set,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSbcsSets.pending, (state) => {
        state.sbcsSets.status = "pending";
      })
      .addCase(fetchSbcsSets.fulfilled, (state, action) => {
        state.sbcsSets.status = "fulfilled";
        if (action.payload) state.sbcsSets.data = action.payload;
      })
      .addCase(fetchSbcsSets.rejected, (state) => {
        state.sbcsSets.status = "rejected";
      });
  },
});

export const getFilteredSbcSetsSelector = (state: IRootState) => {
  let notCompletedSbcs: SbcSet[] = [];
  let completedSbcs: SbcSet[] = [];
  let hiddenSbcs: SbcSet[] = [];

  state.sbcsSetsReducer.sbcsSets.data.forEach((sbc) => {
    if (sbc.isHidden) {
      hiddenSbcs.push(sbc);
    } else if (sbc.solved && sbc.repeatabilityMode !== "UNLIMITED") {
      completedSbcs.push(sbc);
    } else {
      notCompletedSbcs.push(sbc);
    }
  });

  if (state.sbcsSetsReducer.currentCategory.label === "Favorites") {
    notCompletedSbcs = notCompletedSbcs.filter((sbc) => sbc.isFavorite);
    completedSbcs = completedSbcs.filter((sbc) => sbc.isFavorite);
    hiddenSbcs = hiddenSbcs.filter((sbc) => sbc.isFavorite);
  }

  if (state.sbcsSetsReducer.currentCategory.value) {
    notCompletedSbcs = notCompletedSbcs.filter(
      (sbc) => sbc.categoryId === state.sbcsSetsReducer.currentCategory.value,
    );
    completedSbcs = completedSbcs.filter(
      (sbc) => sbc.categoryId === state.sbcsSetsReducer.currentCategory.value,
    );
    hiddenSbcs = hiddenSbcs.filter(
      (sbc) => sbc.categoryId === state.sbcsSetsReducer.currentCategory.value,
    );
  }

  const pcPrice = state.userReducer.user.platform === "PC";
  switch (state.sbcsSetsReducer.currentSort.value) {
    case "newest":
      notCompletedSbcs.sort((a, b) => b.startTime - a.startTime);
      completedSbcs.sort((a, b) => b.startTime - a.startTime);
      hiddenSbcs.sort((a, b) => b.startTime - a.startTime);
      break;
    case "expiration":
      notCompletedSbcs.sort(
        (a, b) =>
          (a.endTime === 0 ? Number.MAX_SAFE_INTEGER : a.endTime) -
          (b.endTime === 0 ? Number.MAX_SAFE_INTEGER : b.endTime),
      );
      completedSbcs.sort(
        (a, b) =>
          (a.endTime === 0 ? Number.MAX_SAFE_INTEGER : a.endTime) -
          (b.endTime === 0 ? Number.MAX_SAFE_INTEGER : b.endTime),
      );
      hiddenSbcs.sort(
        (a, b) =>
          (a.endTime === 0 ? Number.MAX_SAFE_INTEGER : a.endTime) -
          (b.endTime === 0 ? Number.MAX_SAFE_INTEGER : b.endTime),
      );
      break;
    case "most-upvoted":
      notCompletedSbcs.sort((a, b) => b.upVotes - a.upVotes);
      completedSbcs.sort((a, b) => b.upVotes - a.upVotes);
      hiddenSbcs.sort((a, b) => b.upVotes - a.upVotes);
      break;
    case "price-high-to-low":
      if (pcPrice) {
        notCompletedSbcs.sort((a, b) => b.pcPrice - a.pcPrice);
        completedSbcs.sort((a, b) => b.pcPrice - a.pcPrice);
        hiddenSbcs.sort((a, b) => b.pcPrice - a.pcPrice);
      } else {
        notCompletedSbcs.sort((a, b) => b.psPrice - a.psPrice);
        completedSbcs.sort((a, b) => b.psPrice - a.psPrice);
        hiddenSbcs.sort((a, b) => b.psPrice - a.psPrice);
      }
      break;
    case "price-low-to-high":
      if (pcPrice) {
        notCompletedSbcs.sort((a, b) => a.pcPrice - b.pcPrice);
        completedSbcs.sort((a, b) => a.pcPrice - b.pcPrice);
        hiddenSbcs.sort((a, b) => a.pcPrice - b.pcPrice);
      } else {
        notCompletedSbcs.sort((a, b) => a.psPrice - b.psPrice);
        completedSbcs.sort((a, b) => a.psPrice - b.psPrice);
        hiddenSbcs.sort((a, b) => a.psPrice - b.psPrice);
      }
      break;
    case "in-progress":
      notCompletedSbcs.sort((a, b) => a.solvedCount - b.solvedCount);
      completedSbcs.sort((a, b) => a.solvedCount - b.solvedCount);
      hiddenSbcs.sort((a, b) => a.solvedCount - b.solvedCount);
      break;
    case "meta-rating":
      notCompletedSbcs.sort(
        (a, b) =>
          b.reward?.metaRating?.metarating - a.reward?.metaRating?.metarating,
      );
      completedSbcs.sort(
        (a, b) =>
          b.reward?.metaRating?.metarating - a.reward?.metaRating?.metarating,
      );
      hiddenSbcs.sort(
        (a, b) =>
          b.reward?.metaRating?.metarating - a.reward?.metaRating?.metarating,
      );
      break;
  }

  if (state.sbcsSetsReducer.searchTerm) {
    notCompletedSbcs = notCompletedSbcs.filter((sbc) =>
      includesIgnoringAccents(sbc.name, state.sbcsSetsReducer.searchTerm),
    );
    completedSbcs = completedSbcs.filter((sbc) =>
      includesIgnoringAccents(sbc.name, state.sbcsSetsReducer.searchTerm),
    );
    hiddenSbcs = hiddenSbcs.filter((sbc) =>
      includesIgnoringAccents(sbc.name, state.sbcsSetsReducer.searchTerm),
    );
  }

  return {
    notCompletedSbcs,
    completedSbcs,
    hiddenSbcs,
  };
};

export const {
  clearSbcSets,
  updateSbcSolved,
  updateSbcSetSolved,
  updateSbcSetHidden,
  updateSbcSetFavorite,
  upvote,
  downvote,
  updateCategory,
  updateSort,
  updateSearchTerm,
} = sbcSetsSlice.actions;
export const getSbcSetsSelector = (state: IRootState) => state.sbcsSetsReducer;
export default sbcSetsSlice.reducer;
